// src/schemas/customer.ts
import { z } from "zod";
var CustomerSchema = z.object({
  id: z.string().cuid(),
  name: z.string().min(1, "\u9867\u5BA2\u540D\u306F\u5FC5\u9808\u3067\u3059"),
  kana: z.string().optional(),
  address: z.string().optional(),
  phone: z.string().optional(),
  invoiceRegistrationNo: z.string().regex(/^T\d{13}$/, "T + 13\u6841\u306E\u6570\u5B57").optional(),
  notes: z.string().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var CreateCustomerSchema = CustomerSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
var UpdateCustomerSchema = CreateCustomerSchema.partial();

// src/schemas/document.ts
import { z as z2 } from "zod";
var DocumentTypeSchema = z2.enum([
  "INVOICE",
  "ESTIMATE",
  "RECEIPT",
  "PAYMENT_RECEIPT",
  "LAND_CALC",
  "BUILDING_CALC",
  "GUARANTEE_INVOICE",
  "CUSTOM"
]);
var DocumentStatusSchema = z2.enum([
  "DRAFT",
  "REVIEW",
  "ISSUED",
  "PDF_READY",
  "TIMESTAMPED",
  "PAID",
  "CANCELLED"
]);
var VersionStatusSchema = z2.enum([
  "DRAFT",
  "SIGNED",
  "TIMESTAMPED",
  "SUPERSEDED"
]);
var TaxCategorySchema = z2.enum([
  "STANDARD",
  // 10%
  "REDUCED",
  // 8%
  "EXEMPT",
  "WITHHOLDING"
]);
var FileTypeSchema = z2.enum([
  "SOURCE_JSON",
  "EXCEL_SNAPSHOT",
  "PDF_DRAFT",
  "PDF_SIGNED",
  "PDF_TIMESTAMPED"
]);
var DocumentLineSchema = z2.object({
  id: z2.string().cuid().optional(),
  lineNo: z2.number().int().positive(),
  description: z2.string().min(1),
  quantity: z2.number().positive(),
  unitPrice: z2.number().nonnegative(),
  amount: z2.number().nonnegative(),
  taxCategory: TaxCategorySchema.default("STANDARD")
});
var CreateDocumentSchema = z2.object({
  matterId: z2.string().cuid(),
  documentType: DocumentTypeSchema,
  issueDate: z2.coerce.date().optional(),
  dueDate: z2.coerce.date().optional(),
  taxRate: z2.number().min(0).max(1).default(0.1),
  lines: z2.array(DocumentLineSchema).min(1, "\u660E\u7D30\u884C\u306F1\u884C\u4EE5\u4E0A\u5FC5\u8981\u3067\u3059"),
  templateName: z2.string().optional(),
  customFields: z2.record(z2.unknown()).optional()
});
var DocumentSchema = z2.object({
  id: z2.string().cuid(),
  matterId: z2.string().cuid(),
  documentType: DocumentTypeSchema,
  documentNo: z2.string(),
  status: DocumentStatusSchema,
  issueDate: z2.coerce.date().nullable(),
  dueDate: z2.coerce.date().nullable(),
  subtotal: z2.number(),
  tax: z2.number(),
  total: z2.number(),
  taxRate: z2.number(),
  cancelledAt: z2.coerce.date().nullable(),
  cancelReason: z2.string().nullable(),
  createdById: z2.string(),
  createdAt: z2.coerce.date(),
  updatedAt: z2.coerce.date()
});
var GeneratePdfSchema = z2.object({
  documentId: z2.string().cuid(),
  versionId: z2.string().cuid(),
  sign: z2.boolean().default(true),
  timestamp: z2.boolean().default(true)
});
var PdfResultSchema = z2.object({
  fileId: z2.string().cuid(),
  storagePath: z2.string(),
  sha256: z2.string(),
  sizeBytes: z2.number(),
  fileType: FileTypeSchema,
  timestampedAt: z2.coerce.date().nullable(),
  tsaProvider: z2.string().nullable(),
  verificationStatus: z2.enum(["PENDING", "VERIFIED", "FAILED"]).nullable()
});
var InvoiceTemplateDataSchema = z2.object({
  documentType: z2.string(),
  documentNo: z2.string(),
  issueDate: z2.string(),
  dueDate: z2.string().optional(),
  seller: z2.object({
    name: z2.string(),
    address: z2.string().optional(),
    phone: z2.string().optional(),
    registrationNo: z2.string().optional(),
    sealPath: z2.string().optional()
  }),
  buyer: z2.object({
    name: z2.string(),
    address: z2.string().optional()
  }),
  lines: z2.array(z2.object({
    lineNo: z2.number(),
    description: z2.string(),
    quantity: z2.number(),
    unitPrice: z2.number(),
    amount: z2.number(),
    taxCategory: TaxCategorySchema
  })),
  subtotal: z2.number(),
  taxBreakdown: z2.array(z2.object({
    rate: z2.string(),
    base: z2.number(),
    tax: z2.number()
  })),
  total: z2.number(),
  notes: z2.string().optional(),
  bankAccount: z2.object({
    bankName: z2.string(),
    branchName: z2.string(),
    accountType: z2.string(),
    accountNo: z2.string(),
    accountHolder: z2.string()
  }).optional(),
  propertyInfo: z2.object({
    location: z2.string().optional(),
    landType: z2.string().optional(),
    buildingType: z2.string().optional(),
    area: z2.string().optional(),
    structure: z2.string().optional(),
    completionDate: z2.string().optional(),
    registrationCause: z2.string().optional()
  }).optional(),
  guaranteeInfo: z2.object({
    subject: z2.string().optional(),
    guaranteeAmount: z2.number().optional(),
    period: z2.string().optional(),
    guarantor: z2.string().optional(),
    propertyAddress: z2.string().optional()
  }).optional()
});

// src/work-items.ts
import { z as z3 } from "zod";
var ProfessionEnum = z3.enum([
  "judicial",
  "administrative",
  "surveyor",
  "common",
  "other"
]);
var WorkCategoryEnum = z3.enum([
  "fee",
  "registration_tax",
  "stamp",
  "postage",
  "pass_through",
  "withholding",
  "consumption_tax",
  "other"
]);
var TaxCategoryEnum = z3.enum([
  "taxable",
  "non_taxable",
  "tax_exempt",
  "out_of_scope"
]);
var WorkItemSourceEnum = z3.enum([
  "manual",
  "ai",
  "imported",
  "template"
]);
var WorkItemInputSchema = z3.object({
  profession: ProfessionEnum,
  category: WorkCategoryEnum,
  title: z3.string().min(1).max(200),
  description: z3.string().max(2e3).optional(),
  quantity: z3.number().int().min(0).default(1),
  unit_price: z3.number().int().min(0).default(0),
  amount: z3.number().int().min(0).optional(),
  tax_category: TaxCategoryEnum.default("taxable"),
  withholding_target: z3.boolean().default(false),
  is_pass_through: z3.boolean().default(false),
  target_doc_types: z3.array(z3.string()).nullable().optional(),
  source: WorkItemSourceEnum.default("manual"),
  source_ref: z3.string().nullable().optional(),
  confidence: z3.number().min(0).max(1).nullable().optional(),
  ai_metadata: z3.record(z3.unknown()).nullable().optional(),
  sort_order: z3.number().int().optional()
});
var WorkItemBulkInputSchema = z3.object({
  items: z3.array(WorkItemInputSchema).min(1).max(100)
});
var ReorderSchema = z3.object({
  items: z3.array(z3.object({
    id: z3.string(),
    sort_order: z3.number().int().min(0)
  })).min(1)
});
var GenerateRequestSchema = z3.object({
  doc_types: z3.array(z3.string()).min(1),
  mode: z3.enum(["create", "replace_draft", "new_version"]).default("create"),
  work_item_ids: z3.array(z3.string()).optional(),
  idempotency_key: z3.string().uuid().optional(),
  dry_run: z3.boolean().default(false)
});
var PROFESSION_LABEL = {
  judicial: "\u53F8\u6CD5\u66F8\u58EB",
  administrative: "\u884C\u653F\u66F8\u58EB",
  surveyor: "\u8ABF\u67FB\u58EB",
  common: "\u5171\u901A",
  other: "\u305D\u306E\u4ED6"
};
var PROFESSION_COLOR = {
  judicial: "#0f766e",
  administrative: "#7c3aed",
  surveyor: "#b45309",
  common: "#475569",
  other: "#64748b"
};
var WORK_CATEGORY_LABEL = {
  fee: "\u5831\u916C",
  registration_tax: "\u767B\u9332\u7A0E",
  stamp: "\u5370\u7D19\u7A0E",
  postage: "\u90F5\u9001\u6599",
  pass_through: "\u7ACB\u66FF\u91D1",
  withholding: "\u6E90\u6CC9\u6240\u5F97\u7A0E",
  consumption_tax: "\u6D88\u8CBB\u7A0E",
  other: "\u305D\u306E\u4ED6"
};
var TAX_CATEGORY_LABEL = {
  taxable: "\u8AB2\u7A0E",
  non_taxable: "\u975E\u8AB2\u7A0E",
  tax_exempt: "\u514D\u7A0E",
  out_of_scope: "\u5BFE\u8C61\u5916"
};
function getDefaultTaxCategory(category) {
  switch (category) {
    case "fee":
      return "taxable";
    case "registration_tax":
      return "out_of_scope";
    case "stamp":
      return "out_of_scope";
    case "postage":
      return "out_of_scope";
    case "pass_through":
      return "out_of_scope";
    case "withholding":
      return "out_of_scope";
    case "consumption_tax":
      return "out_of_scope";
    default:
      return "taxable";
  }
}
function getDefaultIsPassThrough(category) {
  return ["registration_tax", "stamp", "postage", "pass_through"].includes(category);
}
function getDefaultWithholdingTarget(category, profession) {
  if (category !== "fee") return false;
  return profession === "judicial" || profession === "surveyor";
}

// src/tenant-professions.ts
import { z as z4 } from "zod";
var DisplayPositionEnum = z4.enum(["header_right", "header_left", "below_title", "footer"]);
var DisplayFormatEnum = z4.enum(["label_colon_value", "value_only", "label_space_value"]);
var TenantProfessionInputSchema = z4.object({
  qualification_label: z4.string().min(1).max(100),
  /** tenant_profession_holders.id（この資格種別の既定資格者）。同一テナント・同一資格種別の行のみ許可 */
  holder_id: z4.string().nullable().optional(),
  display_order: z4.number().int().min(0).optional()
});
var TenantProfessionCreateSchema = TenantProfessionInputSchema.extend({
  profession: z4.string().min(1).max(50).regex(/^[a-z][a-z0-9_]*$/).optional()
});
var ProfessionRuleInputSchema = z4.object({
  profession: z4.string().min(1),
  field_key: z4.string().min(1).max(50).regex(/^[a-z][a-z0-9_]*$/),
  label: z4.string().min(1).max(100),
  value: z4.string().max(200).nullable().optional(),
  applies_to_doc_types: z4.array(z4.string()).nullable().optional(),
  display_position: DisplayPositionEnum.default("header_right"),
  display_format: DisplayFormatEnum.default("label_colon_value"),
  display_order: z4.number().int().min(0).optional()
});
var ProfessionRulePatchSchema = ProfessionRuleInputSchema.partial();
var ProfessionRuleSchema = ProfessionRuleInputSchema.extend({
  id: z4.string(),
  value: z4.string().nullable(),
  applies_to_doc_types: z4.array(z4.string()).nullable(),
  display_order: z4.number().int().min(0),
  is_builtin: z4.boolean(),
  created_at: z4.string().optional(),
  updated_at: z4.string().optional()
});
var TenantProfessionSchema = TenantProfessionInputSchema.extend({
  id: z4.string(),
  tenant_id: z4.string(),
  profession: z4.string(),
  display_order: z4.number().int().min(0),
  /** 1 = 組み込み3資格（司法書士・行政書士・土地家屋調査士）。削除不可・profession値変更不可 */
  is_builtin: z4.boolean(),
  updated_at: z4.string(),
  /** 既定資格者（holder_id が指す名簿行）の表示用まとめ */
  holder: z4.object({
    id: z4.string(),
    name: z4.string(),
    registration_number: z4.string().nullable(),
    invoice_registration_number: z4.string().nullable()
  }).nullable(),
  rules: z4.array(ProfessionRuleSchema)
});
var ProfessionHolderInputSchema = z4.object({
  name: z4.string().min(1).max(100),
  professional_registration_number: z4.string().max(100).nullable().optional(),
  invoice_registration_number: z4.string().max(50).nullable().optional(),
  display_order: z4.number().int().min(0).optional()
});
var ProfessionHolderPatchSchema = ProfessionHolderInputSchema.partial();
var ProfessionHolderSchema = z4.object({
  id: z4.string(),
  profession: z4.string(),
  name: z4.string(),
  professional_registration_number: z4.string().nullable(),
  invoice_registration_number: z4.string().nullable(),
  is_default: z4.boolean(),
  display_order: z4.number().int().min(0)
});

// src/tenant-document-types.ts
import { z as z5 } from "zod";
var DocumentTypeKindEnum = z5.enum(["invoice", "estimate", "calc_sheet"]);
var TenantDocumentTypeCreateSchema = z5.object({
  doc_type: z5.string().min(1).max(50).regex(/^[a-z][a-z0-9_]*$/).optional(),
  label: z5.string().min(1).max(100),
  profession: z5.string().min(1).max(50).nullable().optional(),
  kind: DocumentTypeKindEnum.default("invoice"),
  withholding_eligible: z5.boolean().default(true),
  seq_key: z5.string().min(1).max(50).optional(),
  display_order: z5.number().int().min(0).optional()
});
var TenantDocumentTypeUpdateSchema = z5.object({
  label: z5.string().min(1).max(100).optional(),
  display_order: z5.number().int().min(0).optional(),
  withholding_eligible: z5.boolean().optional()
});
var TenantDocumentTypeSchema = z5.object({
  id: z5.string(),
  tenant_id: z5.string(),
  doc_type: z5.string(),
  label: z5.string(),
  profession: z5.string().nullable(),
  kind: DocumentTypeKindEnum,
  withholding_eligible: z5.boolean(),
  seq_key: z5.string(),
  seq_prefix: z5.string(),
  /** 1 = 組み込み7書類種別。削除不可・doc_type/profession/kind値変更不可 */
  is_builtin: z5.boolean(),
  display_order: z5.number().int().min(0),
  created_at: z5.string()
});

// src/ai.ts
import { z as z6 } from "zod";
var JsonPatchOpSchema = z6.object({
  op: z6.enum(["add", "remove", "replace", "move", "copy", "test"]),
  path: z6.string(),
  value: z6.unknown().optional(),
  from: z6.string().optional()
});
var JsonPatchSchema = z6.array(JsonPatchOpSchema);
var ChatTurnSchema = z6.object({
  role: z6.enum(["user", "assistant"]),
  content: z6.string().min(1).max(4e3)
});
var AiTemplateEditRequestSchema = z6.object({
  template_id: z6.string().nullable().optional(),
  instruction: z6.string().min(1).max(2e3),
  selected_block_id: z6.string().nullable().optional(),
  template: z6.record(z6.unknown()),
  /** 自己検証（必須ブロック判定）に使う doc_type。省略時は judicial_invoice 相当で判定する */
  doc_type: z6.string().optional(),
  /** ヒアリング/チャットの往復履歴（新規）。turn はサーバーが算出する */
  conversation: z6.array(ChatTurnSchema).max(20).default([])
});
var AiTemplateEditClarifyResponseSchema = z6.object({
  type: z6.literal("clarify"),
  operation_id: z6.string(),
  question: z6.string(),
  turn: z6.number().int().min(1)
});
var AiTemplateEditPatchResponseSchema = z6.object({
  type: z6.literal("patch"),
  operation_id: z6.string(),
  patch: JsonPatchSchema,
  summary: z6.string()
});
var AiTemplateEditResponseSchema = z6.discriminatedUnion("type", [
  AiTemplateEditClarifyResponseSchema,
  AiTemplateEditPatchResponseSchema
]);
var AiTemplateGenerateRequestSchema = z6.object({
  doc_type: z6.string().min(1),
  /** 書類の説明（直近のユーザー発言。1ターン目は最初の説明、以降は質問への回答） */
  instruction: z6.string().min(1).max(4e3),
  /** 構造化ヒアリングの結果 */
  options: z6.object({
    with_line_items: z6.boolean().optional(),
    with_withholding: z6.boolean().optional(),
    with_bank_info: z6.boolean().optional(),
    orientation: z6.enum(["portrait", "landscape"]).optional()
  }).optional(),
  /** 対話ヒアリングの往復履歴（新規）。turn はサーバーが算出する */
  conversation: z6.array(ChatTurnSchema).max(20).default([])
});
var AiTemplateGenerateClarifyResponseSchema = z6.object({
  type: z6.literal("clarify"),
  operation_id: z6.string(),
  question: z6.string(),
  turn: z6.number().int().min(1)
});
var AiTemplateGeneratedResponseSchema = z6.object({
  type: z6.literal("generated"),
  operation_id: z6.string(),
  template: z6.record(z6.unknown()),
  summary: z6.string(),
  /** AI出力が自己検証を最終リトライまで通らずプリセット骨格にフォールバックした場合 true */
  fallback: z6.boolean().optional()
});
var AiTemplateGenerateResponseSchema = z6.discriminatedUnion("type", [
  AiTemplateGenerateClarifyResponseSchema,
  AiTemplateGeneratedResponseSchema
]);
var AiTemplateImportRequestSchema = z6.object({
  doc_type: z6.string().min(1),
  /** POST /templates/assets で保存済みの下敷き画像 */
  asset_id: z6.string().min(1),
  page: z6.object({
    width_mm: z6.number().positive(),
    height_mm: z6.number().positive(),
    orientation: z6.enum(["portrait", "landscape"])
  }),
  text_items: z6.array(
    z6.object({
      text: z6.string(),
      x_mm: z6.number(),
      y_mm: z6.number(),
      width_mm: z6.number(),
      font_size_pt: z6.number()
    })
  ).max(500),
  /** 任意の補足（例:「明細は源泉徴収あり」） */
  instruction: z6.string().max(2e3).optional()
});
var AiTemplateImportResponseSchema = z6.object({
  operation_id: z6.string(),
  /** fallback 時は骨組みなし（従来のなぞり方式で開始） */
  template: z6.record(z6.unknown()).nullable(),
  summary: z6.string(),
  fallback: z6.boolean().optional()
});

// src/format.ts
function formatJPY(amount) {
  return `\xA5${amount.toLocaleString("ja-JP")}`;
}

// src/datetime.ts
function formatJSTDateTime(isoUtc) {
  return new Intl.DateTimeFormat("ja-JP", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  }).format(new Date(isoUtc));
}
function formatJSTDate(isoUtc) {
  return new Intl.DateTimeFormat("ja-JP", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).format(new Date(isoUtc));
}
function formatWareki(isoDate) {
  const d = /* @__PURE__ */ new Date(`${isoDate.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return isoDate;
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  if (y < 2019 || y === 2019 && m < 5) {
    return `${y}\u5E74${m}\u6708${day}\u65E5`;
  }
  const reiwaYear = y - 2018;
  return `\u4EE4\u548C${reiwaYear === 1 ? "\u5143" : reiwaYear}\u5E74${m}\u6708${day}\u65E5`;
}

// src/html-renderer.ts
var DEFAULT_TRANSFER_NOTE = "\u203B\u632F\u8FBC\u624B\u6570\u6599\u306F\u304A\u5BA2\u69D8\u306B\u3066\u3054\u8CA0\u62C5\u9858\u3044\u307E\u3059\u3002";
function escHtml(s) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
function fmtCurrency(n) {
  return Number.isFinite(n) ? Math.trunc(n).toLocaleString("ja-JP") : "";
}
function getNestedValue(obj, path) {
  const parts = path.split(".");
  let cur = obj;
  for (const p of parts) {
    if (cur === null || cur === void 0) return void 0;
    if (typeof cur !== "object") return void 0;
    cur = cur[p];
  }
  return cur;
}
function interpolate(text, data) {
  return text.replace(/\{\{([^}]+)\}\}/g, (_, path) => {
    const v = getNestedValue(data, path.trim());
    if (v === void 0 || v === null) return "";
    return String(v);
  });
}
function resolveCellValue(raw, data, format) {
  if (raw === null || raw === void 0 || raw === "") return "";
  if (typeof raw === "boolean") return raw ? "\u306F\u3044" : "\u3044\u3044\u3048";
  if (typeof raw === "number") {
    return format === "currency" || format === "number" ? fmtCurrency(raw) : String(raw);
  }
  const m = /^\{\{([^}]+)\}\}$/.exec(raw.trim());
  if (m) {
    const v = getNestedValue(data, m[1].trim());
    const n = typeof v === "number" ? v : 0;
    return format === "currency" ? fmtCurrency(n) : String(n);
  }
  return interpolate(raw, data);
}
function resolveValueKey(key, data) {
  const v = getNestedValue(data, key);
  return typeof v === "number" ? v : 0;
}
function renderTitle(block) {
  const fs = block.style?.font_size ?? 16;
  const align = block.style?.align ?? "center";
  const bold = block.style?.bold !== false;
  const underline = block.style?.underline ? "text-decoration:underline;" : "";
  const ls = block.style?.letter_spacing ? `letter-spacing:${block.style.letter_spacing};` : "";
  const ws = ls ? "white-space:nowrap;" : "";
  return `<div class="blk-title" style="font-size:${fs}pt;text-align:${align};font-weight:${bold ? "bold" : "normal"};${underline}${ls}${ws}margin-bottom:6pt;">${escHtml(block.text)}</div>`;
}
function renderText(block, data) {
  const fs = block.style?.font_size ?? 10;
  const align = block.style?.align ?? "left";
  const bold = block.style?.bold ? "font-weight:bold;" : "";
  const ruleCss = block.style?.rule ? "border-bottom:1.5px solid #000;padding-bottom:2pt;" : "";
  const underline = block.style?.underline ? "text-decoration:underline;" : "";
  const html = escHtml(interpolate(block.text, data)).replace(/\n/g, "<br>");
  return `<div style="font-size:${fs}pt;text-align:${align};${bold}${underline}${ruleCss}margin-bottom:4pt;">${html}</div>`;
}
function renderKv(block, data) {
  const isHorizontal = block.orientation === "horizontal";
  const items = block.items.map((item) => {
    const value = interpolate(String(item.value), data);
    const bold = item.bold ? "font-weight:bold;" : "";
    const ul = item.underline ? "text-decoration:underline;" : "";
    const valueHtml = item.underline ? `<span style="display:inline-block;min-width:160pt;border-bottom:1px solid #000;${bold}">${escHtml(value)}</span>` : `<span style="${bold}${ul}">${escHtml(value)}</span>`;
    if (isHorizontal) {
      return `<div style="display:flex;gap:6pt;margin-bottom:2pt;"><span style="color:#555;min-width:80pt;font-size:9pt;">${escHtml(item.label)}</span>${valueHtml}</div>`;
    }
    return `<div style="margin-bottom:2pt;">${valueHtml}</div>`;
  });
  return `<div style="font-size:10pt;margin-bottom:6pt;">${items.join("")}</div>`;
}
function renderGrid(block, data) {
  if (block.data_source === "line_items") {
    return renderLineItemsGrid(block, data);
  }
  return renderStaticGrid(block, data);
}
function renderLineItemsGrid(block, data) {
  const items = data.line_items ?? [];
  const bordered = block.bordered ?? false;
  const bdr = bordered ? "border:0.5pt solid #000;" : "border-bottom:1px solid #ccc;";
  const hdrBdr = bordered ? "border:0.5pt solid #000;" : "border-bottom:2px solid #333;";
  const totalW = block.columns.reduce((s, c) => s + c.width, 0) || 1;
  const hasGroup = !!block.row_group_label;
  const groupW = hasGroup ? 18 : 0;
  const colgroup = [
    hasGroup ? `<col style="width:${groupW}pt;">` : "",
    ...block.columns.map((c) => `<col style="width:${(c.width / totalW * 100).toFixed(1)}%;">`)
  ].join("");
  const groupHeader = hasGroup ? `<th style="${hdrBdr}background:#f0f0f0;padding:3pt 4pt;text-align:center;font-size:9pt;">${escHtml(block.row_group_label.header ?? "")}</th>` : "";
  const colHeaders = block.columns.map((c) => {
    const align = c.align ?? (c.format === "currency" || c.format === "number" ? "right" : "center");
    return `<th style="${hdrBdr}background:#f0f0f0;padding:3pt 4pt;text-align:${align};font-size:9pt;">${escHtml(c.header)}</th>`;
  }).join("");
  const totalRows = Math.max(items.length, block.min_visual_rows ?? 0);
  let bodyRows = "";
  for (let idx = 0; idx < totalRows; idx++) {
    const item = items[idx];
    const isFirst = idx === 0;
    const groupCell = hasGroup && isFirst ? `<td rowspan="${totalRows}" style="${bdr}writing-mode:vertical-rl;text-align:center;padding:4pt 2pt;font-size:8pt;vertical-align:middle;">${escHtml(block.row_group_label.text)}</td>` : "";
    const dataCells = block.columns.map((c) => {
      let val = "";
      if (item) {
        if (c.id === "item_date") val = item.item_date ?? "";
        else if (c.id === "description") val = item.description;
        else if (c.id === "fee_amount") val = item.fee_amount ? fmtCurrency(item.fee_amount) : "";
        else if (c.id === "tax_amount") val = item.tax_amount ? fmtCurrency(item.tax_amount) : "";
      }
      const align = c.align ?? (c.format === "currency" || c.format === "number" ? "right" : "left");
      return `<td style="${bdr}padding:3pt 4pt;text-align:${align};font-size:9pt;">${val ? escHtml(val) : "&nbsp;"}</td>`;
    }).join("");
    bodyRows += `<tr>${groupCell}${dataCells}</tr>
`;
  }
  const amountColIds = new Set(block.columns.filter((c) => c.format === "currency" || c.format === "number").map((c) => c.id));
  const labelColspan = block.columns.filter((c) => !amountColIds.has(c.id)).length;
  let summaryRows = "";
  for (const row of block.summary ?? []) {
    const groupEmpty = hasGroup ? `<td style="${bdr}padding:3pt 4pt;"></td>` : "";
    const labelCell = labelColspan > 0 ? `<td colspan="${labelColspan}" style="${bdr}padding:3pt 4pt;text-align:right;font-weight:${row.emphasis ? "bold" : "normal"};font-size:9pt;">${escHtml(row.label)}</td>` : "";
    const amountCells = block.columns.filter((c) => amountColIds.has(c.id)).map((c) => {
      const val = resolveCellValue(row.cells[c.id], data, c.format);
      const markerText = row.markers?.[c.id];
      const marker = markerText ? `<span style="font-weight:bold;margin-right:3pt;">${escHtml(markerText)}</span>` : "";
      return `<td style="${bdr}padding:3pt 4pt;text-align:right;font-weight:${row.emphasis ? "bold" : "normal"};font-size:9pt;">${marker}${escHtml(val)}</td>`;
    }).join("");
    summaryRows += `<tr>${groupEmpty}${labelCell}${amountCells}</tr>
`;
  }
  return `<table style="width:100%;border-collapse:collapse;margin-bottom:6pt;">
<colgroup>${colgroup}</colgroup>
<thead><tr>${groupHeader}${colHeaders}</tr></thead>
<tbody>${bodyRows}${summaryRows}</tbody>
</table>`;
}
function renderStaticGrid(block, data) {
  const bordered = block.bordered ?? false;
  const bdr = bordered ? "border:1px solid #000;" : "border-bottom:1px solid #ccc;";
  const hdrBdr = bordered ? "border:1px solid #000;" : "border-bottom:2px solid #333;";
  const totalW = block.columns.reduce((s, c) => s + c.width, 0) || 1;
  const colgroup = block.columns.map((c) => `<col style="width:${(c.width / totalW * 100).toFixed(1)}%;">`).join("");
  const headers = block.columns.map((c) => {
    const align = c.align ?? "center";
    return `<th style="${hdrBdr}background:#f0f0f0;padding:3pt 4pt;text-align:${align};font-size:9pt;">${escHtml(c.header)}</th>`;
  }).join("");
  let bodyRows = "";
  for (const row of block.rows) {
    const bgStyle = row.is_section || row.is_subsection ? "background:#f8fafc;" : row.type === "subtotal" ? "background:#f0fdfa;" : "";
    const cells = block.columns.map((c) => {
      const rawCell = c.id === "category" && row.label ? row.label : row.cells?.[c.id] ?? "";
      const val = typeof rawCell === "string" && rawCell.startsWith("=") ? "" : resolveCellValue(rawCell, data, c.format);
      const align = c.align ?? (c.format === "currency" || c.format === "number" ? "right" : "left");
      const fw = row.type === "subtotal" || row.is_section ? "font-weight:bold;" : "";
      const indent = c.id === "category" ? `padding-left:${(row.level - 1) * 8}pt;` : "";
      return `<td style="${bdr}${bgStyle}${fw}${indent}padding:3pt 4pt;text-align:${align};font-size:9pt;">${escHtml(val)}</td>`;
    }).join("");
    bodyRows += `<tr>${cells}</tr>
`;
  }
  const amountColIds = new Set(block.columns.filter((c) => c.format === "currency" || c.format === "number").map((c) => c.id));
  const labelColspan = block.columns.filter((c) => !amountColIds.has(c.id)).length;
  let summaryRows = "";
  for (const row of block.summary ?? []) {
    const labelCell = labelColspan > 0 ? `<td colspan="${labelColspan}" style="${bdr}padding:3pt 4pt;text-align:right;font-weight:${row.emphasis ? "bold" : "normal"};font-size:9pt;">${escHtml(row.label)}</td>` : "";
    const amountCells = block.columns.filter((c) => amountColIds.has(c.id)).map((c) => {
      const val = resolveCellValue(row.cells[c.id], data, c.format);
      return `<td style="${bdr}padding:3pt 4pt;text-align:right;font-weight:${row.emphasis ? "bold" : "normal"};font-size:9pt;">${escHtml(val)}</td>`;
    }).join("");
    summaryRows += `<tr>${labelCell}${amountCells}</tr>
`;
  }
  return `<table style="width:100%;border-collapse:collapse;margin-bottom:6pt;">
<colgroup>${colgroup}</colgroup>
<thead><tr>${headers}</tr></thead>
<tbody>${bodyRows}${summaryRows}</tbody>
</table>`;
}
function renderRowContainer(block, data) {
  const cols = block.columns.map((col) => {
    const width = col.width != null ? `${col.width}%` : "auto";
    const align = col.align ?? "left";
    const inner = col.blocks.map((b) => renderBlock(b, data)).join("");
    return `<div style="width:${width};text-align:${align};flex:${col.width != null ? "0 0 " + col.width + "%" : "1"};">${inner}</div>`;
  }).join("");
  return `<div style="display:flex;gap:0;margin-bottom:6pt;">${cols}</div>`;
}
function renderFreeBand(block, data) {
  const rowUnit = block.row_unit ?? 4;
  const children = block.children.map((c) => {
    const left = c.pos.col / 12 * 100;
    const width = c.pos.colSpan / 12 * 100;
    const top = c.pos.row * rowUnit;
    const minHeight = c.pos.rowSpan * rowUnit;
    return `<div style="position:absolute;left:${left.toFixed(4)}%;width:${width.toFixed(4)}%;top:${top}mm;min-height:${minHeight}mm;">${renderBlock(c.block, data)}</div>`;
  }).join("\n");
  return `<div style="position:relative;height:${block.height}mm;margin-bottom:6pt;">${children}</div>`;
}
function renderRecipient(block, data) {
  const dataKey = block.data_key ?? "client.name";
  const honorific = block.honorific ?? "\u69D8";
  const showUnderline = block.underline !== false;
  const fs = block.style?.font_size ?? 12;
  const rawValue = getNestedValue(data, dataKey);
  const name = rawValue != null ? String(rawValue) : "";
  const underlineCss = showUnderline ? "border-bottom:1px solid #000;display:inline-block;min-width:160pt;" : "";
  return `<div style="font-size:${fs}pt;margin-bottom:4pt;"><span style="${underlineCss}">${escHtml(name)}</span>\u3000${escHtml(honorific)}</div>`;
}
var ISSUER_KIND_PATH = {
  office_name_short: "office.name_short",
  office_name_main: "office.name_main",
  postal: "office.postal",
  address: "office.address",
  issuer_name: "",
  // 特殊処理: qualification + name
  tel: "office.tel",
  fax: "office.fax",
  registration_number: "invoice.registration_number"
};
var ISSUER_KIND_PREFIX = {
  postal: "\u3012",
  tel: "TEL ",
  fax: "FAX ",
  registration_number: "\u767B\u9332\u756A\u53F7"
};
function renderIssuerInfo(block, data) {
  const fs = block.style?.font_size ?? 9;
  const align = block.style?.align ?? "right";
  const lines = block.lines.filter((line) => line.visible !== false).map((line) => {
    const bold = line.bold ? "font-weight:bold;" : "";
    let text = "";
    let lineFs = fs;
    let topGap = "";
    if (line.kind === "custom") {
      text = line.text ?? "";
    } else if (line.kind === "issuer_name") {
      const qual = getNestedValue(data, "issuer.qualification");
      const name = getNestedValue(data, "issuer.name");
      const prefix = line.text ?? "";
      text = `${prefix}${qual != null ? String(qual) : ""}\u3000${name != null ? String(name) : ""}`;
      lineFs = fs + 1;
    } else {
      const path = ISSUER_KIND_PATH[line.kind] ?? "";
      let raw = path ? getNestedValue(data, path) : void 0;
      if (line.kind === "office_name_main" && (raw == null || raw === "")) {
        raw = getNestedValue(data, "office.name");
      }
      let value = raw != null ? String(raw) : "";
      if (line.kind === "postal" && value) {
        value = value.replace(
          /[0-9-]/g,
          (ch) => ch === "-" ? "\uFF0D" : String.fromCharCode(ch.charCodeAt(0) + 65248)
        );
      }
      const prefix = line.text ?? (ISSUER_KIND_PREFIX[line.kind] ?? "");
      text = value ? `${prefix}${value}` : "";
      if (line.kind === "registration_number") topGap = `margin-top:${fs}pt;`;
    }
    return `<div style="font-size:${lineFs}pt;text-align:${align};${bold}${topGap}margin-bottom:2pt;">${escHtml(text)}</div>`;
  }).join("");
  return `<div style="margin-bottom:4pt;">${lines}</div>`;
}
function renderSettlement(block, data) {
  const bordered = block.bordered !== false;
  const outerBdr = bordered ? "border:0.75pt solid #000;" : "";
  const cellBdr = bordered ? "border:0.5pt solid #000;" : "border-top:1px solid #ccc;";
  const rowsWithLabel = block.rows.map((row, idx) => {
    const value = resolveValueKey(row.value_key, data);
    const displayVal = fmtCurrency(value);
    const labelTd = idx === 0 && block.vertical_label ? `<td rowspan="${block.rows.length}" style="writing-mode:vertical-rl;text-align:center;font-weight:bold;padding:6pt 4pt;font-size:10pt;${cellBdr}">${escHtml(block.vertical_label)}</td>` : "";
    return `<tr>
  ${labelTd}
  <td style="padding:4pt 8pt;font-size:9pt;${cellBdr}">${escHtml(row.label)}</td>
  <td style="padding:4pt 8pt;font-size:9pt;color:#555;${cellBdr}">${escHtml(row.formula_text)}</td>
  <td style="padding:4pt 2pt;text-align:center;font-size:9pt;font-weight:bold;${cellBdr}">${escHtml(row.ref ?? "")}</td>
  <td style="padding:4pt 8pt;text-align:right;font-size:10pt;font-weight:${row.ref ? "bold" : "normal"};${cellBdr}">\xA5${escHtml(displayVal)}</td>
</tr>`;
  }).join("");
  const colgroup = [
    block.vertical_label ? `<col style="width:18pt;">` : "",
    `<col style="width:22%;">`,
    `<col>`,
    `<col style="width:20pt;">`,
    `<col style="width:30%;">`
  ].join("");
  return `<table style="width:100%;border-collapse:collapse;table-layout:fixed;${outerBdr}margin-bottom:6pt;">
<colgroup>${colgroup}</colgroup>
<tbody>${rowsWithLabel}</tbody>
</table>`;
}
function renderBlock(block, data) {
  switch (block.type) {
    case "title":
      return renderTitle(block);
    case "text":
      return renderText(block, data);
    case "spacer":
      return `<div style="height:${block.height}pt;"></div>`;
    case "divider":
      return `<hr style="border:none;border-top:1px solid #333;margin:4pt 0;">`;
    case "kv_block":
      return renderKv(block, data);
    case "computed_value": {
      const dataKey = block.data_key ?? "";
      const v = dataKey ? getNestedValue(data, dataKey) : void 0;
      const n = typeof v === "number" ? v : 0;
      const display = block.format === "currency" ? `\xA5${fmtCurrency(n)}` : String(Math.trunc(n));
      const fs = block.style?.font_size ?? 14;
      const align = block.style?.align ?? "right";
      const bold = block.style?.bold !== false ? "font-weight:bold;" : "";
      return `<div style="font-size:${fs}pt;text-align:${align};${bold}margin-bottom:4pt;">${block.label ? escHtml(block.label) + " " : ""}${escHtml(display)}</div>`;
    }
    case "grid":
      return renderGrid(block, data);
    case "row_container":
      return renderRowContainer(block, data);
    case "settlement":
      return renderSettlement(block, data);
    case "free_band":
      return renderFreeBand(block, data);
    case "recipient":
      return renderRecipient(block, data);
    case "issuer_info":
      return renderIssuerInfo(block, data);
    default:
      return "";
  }
}
function serializeBlocksToHtml(template, data) {
  const { page_config, body } = template;
  const m = page_config.margins;
  const fontUrl = "https://cdn.jsdelivr.net/gh/googlefonts/noto-cjk@main/Sans/SubsetOTF/JP/NotoSansJP-Regular.otf";
  const serifFontUrl = "https://cdn.jsdelivr.net/gh/googlefonts/noto-cjk@main/Serif/SubsetOTF/JP/NotoSerifJP-Regular.otf";
  const css = `
@font-face{font-family:'NotoSansJP';src:url('${fontUrl}') format('opentype');}
@font-face{font-family:'NotoSerifJP';src:url('${serifFontUrl}') format('opentype');}
@page{size:A4 ${page_config.orientation};margin:${m.top}mm ${m.right}mm ${m.bottom}mm ${m.left}mm;}
*{box-sizing:border-box;}
html,body{font-family:'NotoSansJP','Hiragino Sans','Yu Gothic',sans-serif;font-size:10pt;color:#000;margin:0;padding:0;}
.blk-title{font-family:'NotoSerifJP','Hiragino Mincho ProN','Yu Mincho',serif;}
table{border-collapse:collapse;}
`;
  const blocksHtml = body.blocks.map((b) => renderBlock(b, data)).join("\n");
  const backgroundSrc = typeof data["background_src"] === "string" ? data["background_src"] : "";
  const backgroundOpacity = template.background?.opacity ?? 0.35;
  const backgroundHtml = backgroundSrc ? `<img src="${backgroundSrc}" aria-hidden="true" style="position:fixed;top:0;left:0;width:100%;height:100%;object-fit:contain;object-position:top left;opacity:${backgroundOpacity};z-index:-1;pointer-events:none;">` : "";
  return `<!DOCTYPE html>
<html lang="ja">
<head>
<meta charset="UTF-8">
<style>${css}</style>
</head>
<body>${backgroundHtml}${blocksHtml}</body>
</html>`;
}

// src/fee-calc-sheet.ts
function escHtml2(value) {
  return String(value ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\"/g, "&quot;");
}
function fmtQty(value) {
  if (Number.isInteger(value)) return String(value);
  return String(value).replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "");
}
function money(value) {
  return formatJPY(value);
}
function moneyDeduction(value) {
  return value > 0 ? `\u25B3 ${money(value)}` : "";
}
function tr(cells, opts = {}) {
  const leftCols = opts.leftCols ?? /* @__PURE__ */ new Set([0]);
  return `<tr class="${opts.className ?? ""}">${cells.map((cell, i) => `<td class="${leftCols.has(i) ? "text" : "num"}">${cell}</td>`).join("")}</tr>`;
}
var WORK_RATE_HEADERS = ["\u4F5C\u696D\u7A2E\u5225", "\u6570\u91CF\u53CA\u3073\u4EF6\u6570", "\u57FA\u6E96", "\u52A0\u6E1B\u7387", "\u8A08", "\u6458\u8981"];
var WORK_RATE_LEFT_COLS = /* @__PURE__ */ new Set([0, 1, 5]);
function workRateRowCells(row) {
  return [
    escHtml2(row.label),
    `${fmtQty(row.quantity)}${escHtml2(row.unit)}`,
    money(row.base_amount),
    row.factor === null ? "" : escHtml2(row.factor),
    money(row.amount),
    escHtml2(row.note)
  ];
}
function workRateBlankCells(label, note) {
  return [escHtml2(label), "", "", "", "", escHtml2(note ?? "")];
}
function workRateBody(result, schedule) {
  const rowsByKey = /* @__PURE__ */ new Map();
  for (const row of result.rows) {
    const list = rowsByKey.get(row.key);
    if (list) list.push(row);
    else rowsByKey.set(row.key, [row]);
  }
  const bodyRows = [];
  for (const section of schedule.sections) {
    const items = schedule.items.filter((item) => item.section === section.key);
    const sectionRows = [];
    for (const item of items) {
      const entered = rowsByKey.get(item.key) ?? [];
      if (item.pricing.type === "area_tiered") {
        for (const row of entered) sectionRows.push(tr(workRateRowCells(row), { leftCols: WORK_RATE_LEFT_COLS }));
        continue;
      }
      if (entered.length === 0) {
        sectionRows.push(tr(workRateBlankCells(item.label, item.note), { className: "blank", leftCols: WORK_RATE_LEFT_COLS }));
      } else {
        for (const row of entered) sectionRows.push(tr(workRateRowCells(row), { leftCols: WORK_RATE_LEFT_COLS }));
      }
    }
    if (sectionRows.length === 0) continue;
    bodyRows.push(`<tr class="section-head"><td colspan="6">${escHtml2(section.label)}</td></tr>`);
    bodyRows.push(...sectionRows);
    const subtotal = result.section_subtotals[section.key] ?? 0;
    bodyRows.push(tr(
      ["\u5C0F\u3000\u8A08", "", "", "", money(subtotal), ""],
      { className: "subtotal", leftCols: WORK_RATE_LEFT_COLS }
    ));
  }
  if (result.travel_km > 0) {
    bodyRows.push(tr(
      ["\u65C5\u8CBB", `${fmtQty(result.travel_km)}km`, "", "", result.travel_amount > 0 ? money(result.travel_amount) : "", escHtml2(schedule.travel_note ?? "")],
      { leftCols: WORK_RATE_LEFT_COLS }
    ));
  }
  const trailingLabel = schedule.sections.length + 1;
  const detailTable = `<table class="detail work-rate"><thead><tr>${WORK_RATE_HEADERS.map((h) => `<th>${escHtml2(h)}</th>`).join("")}</tr></thead><tbody>${bodyRows.join("")}</tbody></table>`;
  const totalRows = [
    [`${trailingLabel}\u3000\u4E0A\u8A18\u306E\u5408\u8A08\uFF08\u4F5C\u696D\uFF0B\u65C5\u8CBB\uFF09`, money(result.gross_total)],
    [`${trailingLabel + 1}\u3000\u8ABF\u6574\u984D`, moneyDeduction(result.adjustment_amount)],
    ["\u2460 \u5408\u8A08\u5831\u916C\u984D\uFF08\u8ABF\u6574\u5F8C\uFF09", money(result.fee_total)],
    ["\u2461 \u6D88\u8CBB\u7A0E\uFF0810%\uFF09", money(result.consumption_tax)],
    ["\u2462 \u5C0F\u8A08\uFF08\u2460\uFF0B\u2461\uFF09", money(result.subtotal_with_tax)],
    ["\u2463 \u6E90\u6CC9\u6240\u5F97\u7A0E", money(result.withholding_tax)],
    ["\u2464 \u5DEE\u5F15\u91D1\u984D\uFF08\u2462\u2212\u2463\uFF09", money(result.net_after_withholding)],
    ["\u3000\u3000\u767B\u9332\u514D\u8A31\u7A0E\u30FB\u5370\u7D19\u7A0E", money(result.registration_tax_amount)],
    ["\u2465 \u7ACB\u66FF\u91D1", money(result.advance_expense_amount)],
    ["\u3000\u3000\u5C0F\u8A08", money(result.expense_subtotal)],
    ["\u2466 \u5408\u8A08\u91D1\u984D\uFF08\u2464\uFF0B\u2465\uFF09", money(result.grand_total)],
    ["\u2467 \u524D\u53D7\u91D1", money(result.prepaid_amount)],
    ["\u2468 \u8ACB\u6C42\u91D1\u984D\uFF08\u2466\u2212\u2467\uFF09", money(result.billed_total), true]
  ];
  return `${detailTable}
    <table class="totals"><tbody>${totalRows.map(([label, value, emphasis]) => `<tr class="${emphasis ? "emphasis" : ""}"><td>${escHtml2(label)}</td><td>${value}</td></tr>`).join("")}</tbody></table>`;
}
var GUARANTEE_HEADERS = [
  "\u50B5\u52D9\u8005\u540D",
  "\u9867\u5BA2\u756A\u53F7",
  "\u79FB\u8EE2\u91D1\u984D\uFF08\u8AB2\u7A0E\u4FA1\u683C\uFF09",
  "",
  "\u57FA\u672C\u5831\u916C\u7B49",
  "\u500B\u6570\u52A0\u7B97\u30FB\u95B2\u89A7",
  "\u8B04\u672C",
  "\u65E5\u5F53",
  "\u65C5\u8CBB",
  "\u6E90\u6CC9\u8AB2\u7A0E\u5BFE\u8C61\u984D",
  "\u6D88\u8CBB\u7A0E",
  "\u767B\u9332\u514D\u8A31\u7A0E",
  "\u79FB\u8EE2\u8CBB\u7528\u91D1\u984D"
];
function guaranteeDetailTable(blocks) {
  const rows = blocks.map((b) => {
    const summaryCell = `<td class="num" rowspan="2"><div class="cell-stack"><div><span class="k">\u5408\u8A08</span>${money(b.block_total)}</div><div><span class="k">\u6E90\u6CC9\u6240\u5F97\u7A0E</span>${money(b.withholding_tax)}</div><div><span class="k">\u8ACB\u6C42\u984D</span>${money(b.billed_total)}</div></div></td>`;
    const feeRow = `<tr>
      <td class="text" rowspan="2">${escHtml2(b.name || "\u2014")}</td>
      <td class="text" rowspan="2">${escHtml2(b.customer_number || "\u2014")}</td>
      <td class="num" rowspan="2">${money(b.transfer_amount)}</td>
      <td class="row-tag">\u5831\u916C</td>
      <td class="num">${money(b.base_fee)}</td>
      <td class="num">${b.inspection_amount > 0 ? money(b.inspection_amount) : ""}</td>
      <td class="num">${b.transcript_amount > 0 ? money(b.transcript_amount) : ""}</td>
      <td class="num">${b.per_diem > 0 ? money(b.per_diem) : ""}</td>
      <td class="num">${b.travel > 0 ? money(b.travel) : ""}</td>
      <td class="num">${money(b.fee_total)}</td>
      <td class="num"></td>
      <td class="num"></td>
      ${summaryCell}
    </tr>`;
    const taxRow = `<tr>
      <td class="row-tag">\u7A0E</td>
      <td class="num"></td>
      <td class="num">${b.count_surcharge_amount > 0 ? money(b.count_surcharge_amount) : ""}</td>
      <td class="num">${b.transcript_tax_amount > 0 ? money(b.transcript_tax_amount) : ""}</td>
      <td class="num"></td>
      <td class="num"></td>
      <td class="num"></td>
      <td class="num">${money(b.consumption_tax)}</td>
      <td class="num">${b.registration_tax > 0 ? money(b.registration_tax) : ""}</td>
    </tr>`;
    return feeRow + taxRow;
  });
  return `<table class="detail guarantee"><thead><tr>${GUARANTEE_HEADERS.map((h) => `<th>${escHtml2(h)}</th>`).join("")}</tr></thead><tbody>${rows.join("")}</tbody></table>`;
}
function guaranteeBody(result) {
  const stamps = [
    ["\u2460 \u95B2\u89A7\u5370\u7D19\uFF08\u500B\u6570\u52A0\u7B97\uFF09", result.inspection_stamp_total],
    ["\u2461 \u8B04\u672C\u5370\u7D19", result.transcript_stamp_total],
    ["\u2462 \u5370\u7D19\u8A08\uFF08\u2460\uFF0B\u2461\uFF09", result.stamp_total],
    ["\u2463 \u767B\u9332\u514D\u8A31\u7A0E", result.registration_tax_total],
    ["\u2464 \u5408\u8A08\uFF08\u2462\uFF0B\u2463\uFF09", result.tax_breakdown_total]
  ];
  const totals = [
    ["\u7DCF\u5408\u8A08\uFF08\u03A3\u5408\u8A08\uFF09", result.grand_total],
    ["\u6E90\u6CC9\u6240\u5F97\u7A0E\u984D", result.withholding_total],
    ["\u8ACB\u6C42\u91D1\u984D\uFF08\u7DCF\u5408\u8A08\u2212\u6E90\u6CC9\uFF09", result.billed_total],
    ["\u4EEE\u6255\u91D1\u984D", result.advance_paid_amount],
    ["\u632F\u8FBC\u91D1\u984D\uFF08\u8ACB\u6C42\u91D1\u984D\u2212\u4EEE\u6255\uFF09", result.transfer_net_total, true]
  ];
  return `${guaranteeDetailTable(result.blocks)}
    <div class="guarantee-totals"><section><h2>\u5370\u7D19\u30FB\u767B\u9332\u514D\u8A31\u7A0E\u5185\u8A33</h2><table class="totals"><tbody>${stamps.map(([label, value]) => `<tr><td>${escHtml2(label)}</td><td>${money(value)}</td></tr>`).join("")}</tbody></table></section>
    <table class="totals"><tbody>${totals.map(([label, value, emphasis]) => `<tr class="${emphasis ? "emphasis" : ""}"><td>${escHtml2(label)}</td><td>${money(value)}</td></tr>`).join("")}</tbody></table></div>`;
}
function renderFeeCalcSheetHtml(result, data, opts) {
  const title = opts.docType === "land_calculation" ? "\u571F\u5730\u6E2C\u91CF\u5831\u916C\u984D\u8A08\u7B97\u66F8" : opts.docType === "building_calculation" ? "\u5EFA\u7269\u6E2C\u91CF\u5831\u916C\u984D\u8A08\u7B97\u66F8" : "\u4FDD\u8A3C\u5354\u4F1A\u8ACB\u6C42\u66F8";
  const location = result.kind === "work_rate" && typeof data["calc_input"] === "object" ? data["calc_input"] : null;
  const locationHtml = location && (location.location || location.area_note) ? `<p class="location">${location.location ? `\u6240\u5728: ${escHtml2(location.location)}` : ""}${location.location && location.area_note ? "\u3000" : ""}${location.area_note ? escHtml2(location.area_note) : ""}</p>` : "";
  const issueInfo = `<div class="issue-info">\u767A\u884C\u65E5\u3000${escHtml2(data.invoice?.date)}<br>\u66F8\u985E\u756A\u53F7\u3000${escHtml2(data.invoice?.number)}</div>`;
  const issuer = `<div class="issuer">${escHtml2(data.office?.name)}<br>\u3012${escHtml2(data.office?.postal)}\u3000${escHtml2(data.office?.address)}<br>TEL ${escHtml2(data.office?.tel)}\u3000FAX ${escHtml2(data.office?.fax)}<br>${escHtml2(data.issuer?.qualification)} ${escHtml2(data.issuer?.name)}${data.invoice?.registration_number ? `<br>\u767B\u9332\u756A\u53F7\u3000${escHtml2(data.invoice.registration_number)}` : ""}</div>`;
  const page = opts.docType === "guarantee_invoice" ? "landscape" : "portrait";
  if (result.kind === "work_rate" && !opts.schedule) {
    throw new Error("renderFeeCalcSheetHtml: work_rate \u306E\u7D50\u679C\u306B\u306F schedule \u304C\u5FC5\u9808\u3067\u3059");
  }
  const body = result.kind === "work_rate" ? workRateBody(result, opts.schedule) : guaranteeBody(result);
  return `<!DOCTYPE html><html lang="ja"><head><meta charset="UTF-8"><style>
@font-face{font-family:'NotoSansJP';src:url('https://cdn.jsdelivr.net/gh/googlefonts/noto-cjk@main/Sans/SubsetOTF/JP/NotoSansJP-Regular.otf') format('opentype');}@font-face{font-family:'NotoSerifJP';src:url('https://cdn.jsdelivr.net/gh/googlefonts/noto-cjk@main/Serif/SubsetOTF/JP/NotoSerifJP-Regular.otf') format('opentype');}
@page{size:A4 ${page};margin:13mm 12mm;}*{box-sizing:border-box}body{font-family:'NotoSansJP','Hiragino Sans','Yu Gothic',sans-serif;font-size:9pt;color:#000;margin:0}.blk-title{font-family:'NotoSerifJP','Yu Mincho',serif}.header{margin-bottom:10pt}.header-top{display:flex;align-items:flex-start;gap:8pt}.header-spacer{flex:1 1 0;min-width:0}.blk-title{flex:1.6 1 0;min-width:0;text-align:center;font-size:18pt;font-weight:bold;margin:0}.header-right{flex:1 1 0;min-width:0;display:flex;flex-direction:column;gap:7pt}.issue-info{text-align:right;font-size:8pt;line-height:1.7}.recipient{margin-top:10pt;font-size:13pt;border-bottom:1px solid #000;display:inline-block;min-width:255pt;padding:2pt 4pt}.issuer{text-align:right;font-size:8pt;line-height:1.55}.location{margin:0 0 7pt;font-size:9pt}.detail{width:100%;border-collapse:collapse;font-size:8pt}.detail th,.detail td{border:0.5pt solid #000;padding:3pt 4pt;vertical-align:middle}.detail th{text-align:center;background:#f2f2f2;white-space:nowrap}.detail td.num{text-align:right;white-space:nowrap}.detail td.text{text-align:left}.detail tr.section-head td{background:#e9e9e9;font-weight:700;text-align:left}.detail tr.subtotal td{background:#f7f7f7;font-weight:700}.detail tr.blank td.text{color:#555}.guarantee{font-size:6.4pt}.guarantee th,.guarantee td{padding:2.2pt 2.5pt}.guarantee td.row-tag{text-align:center;font-weight:700;background:#f2f2f2;width:22pt}.guarantee .cell-stack{display:flex;flex-direction:column;gap:1.5pt;text-align:right}.guarantee .cell-stack .k{display:block;font-size:5.6pt;color:#444;font-weight:400;text-align:left}.totals{border-collapse:collapse;min-width:330pt;margin:12pt 0 0 auto;font-size:9pt}.totals td{border-bottom:0.5pt solid #999;padding:4pt 7pt}.totals td:last-child{text-align:right;font-weight:600;white-space:nowrap}.totals .emphasis td{font-weight:900;border-top:1pt solid #000;border-bottom:1pt solid #000}.guarantee-totals{display:flex;justify-content:space-between;gap:22pt;align-items:flex-start}.guarantee-totals h2{font-size:8pt;margin:12pt 0 3pt}.guarantee-totals .totals{margin-top:0}.notes{white-space:pre-wrap;font-size:8pt;margin-top:16pt;border-top:0.5pt solid #999;padding-top:5pt}
</style></head><body><header class="header"><div class="header-top"><div class="header-spacer"></div><h1 class="blk-title">${escHtml2(title)}</h1><div class="header-right">${issueInfo}${issuer}</div></div><div class="recipient">${escHtml2(data.client?.name)}\u3000\u69D8</div></header>${locationHtml}${body}${data.notes ? `<footer class="notes">${escHtml2(data.notes)}</footer>` : ""}</body></html>`;
}

// src/simple-settings.ts
var ROLE_COLUMN_IDS = {
  date: ["item_date", "date"],
  description: ["description"],
  fee: ["fee_amount", "fee"],
  tax: ["tax_amount", "tax"]
};
function projectBlock(block, s) {
  switch (block.type) {
    case "title":
      if (s.title_text) block.text = s.title_text;
      break;
    case "recipient":
      if (s.honorific !== void 0) block.honorific = s.honorific;
      break;
    case "issuer_info":
      if (s.visibility?.registration_number === false) {
        for (const line of block.lines) {
          if (line.kind === "registration_number") line.visible = false;
        }
      }
      break;
    case "grid":
      if (s.column_labels) {
        for (const [role, label] of Object.entries(s.column_labels)) {
          if (!label) continue;
          const ids = ROLE_COLUMN_IDS[role] ?? [];
          for (const col of block.columns) {
            if (ids.includes(col.id)) col.header = label;
          }
        }
      }
      break;
    case "row_container":
      for (const column of block.columns) {
        for (const child of column.blocks) projectBlock(child, s);
      }
      break;
    case "free_band":
      for (const child of block.children) projectBlock(child.block, s);
      break;
    default:
      break;
  }
}
function applySimpleSettings(template, settings) {
  const s = settings ?? template.simple_settings;
  if (!s) return template;
  const cloned = JSON.parse(JSON.stringify(template));
  for (const block of cloned.body.blocks) projectBlock(block, s);
  return cloned;
}
function applySimpleOffice(base, settings) {
  const office = settings?.office;
  if (!office) return base;
  const merged = { ...base };
  for (const [key, value] of Object.entries(office)) {
    if (value) merged[key] = value;
  }
  return merged;
}

// src/calc.ts
function computeWithholdingTax(feeSubtotal) {
  return Math.floor(Math.max(feeSubtotal - 1e4, 0) * 0.1021);
}
function computeJudicialInvoice(lines, adjustmentAmount = 0, applyWithholding = true) {
  const fee_subtotal = lines.reduce((s, l) => s + l.fee_amount, 0);
  const tax_subtotal = lines.reduce((s, l) => s + l.tax_amount, 0);
  const consumption_tax = Math.floor(fee_subtotal * 0.1);
  const withholding_tax = applyWithholding ? computeWithholdingTax(fee_subtotal) : 0;
  const total = fee_subtotal + tax_subtotal + consumption_tax - withholding_tax + adjustmentAmount;
  return { fee_subtotal, tax_subtotal, consumption_tax, withholding_tax, total };
}
function computeAdministrativeInvoice(lines, adjustmentAmount = 0, _applyWithholding = true) {
  const fee_subtotal = lines.reduce((s, l) => s + l.fee_amount, 0);
  const tax_subtotal = lines.reduce((s, l) => s + l.tax_amount, 0);
  const consumption_tax = Math.floor(fee_subtotal * 0.1);
  const withholding_tax = 0;
  const total = fee_subtotal + tax_subtotal + consumption_tax + adjustmentAmount;
  return { fee_subtotal, tax_subtotal, consumption_tax, withholding_tax, total };
}
function computeSurveyorInvoice(lines, adjustmentAmount = 0, applyWithholding = true) {
  return computeJudicialInvoice(lines, adjustmentAmount, applyWithholding);
}
var DEFAULT_ESTIMATE_WITHHOLDING = {
  judicial: true,
  administrative: false,
  surveyor: true
};
function computeEstimate(lines, adjustmentAmount = 0, applyWithholding = true, professionWithholding) {
  const map = professionWithholding ?? DEFAULT_ESTIMATE_WITHHOLDING;
  let fee_subtotal = 0;
  let tax_subtotal = 0;
  let consumption_tax = 0;
  let withholding_tax = 0;
  for (const profession of Object.keys(map)) {
    const group = lines.filter((l) => l.profession === profession);
    if (group.length === 0) continue;
    const fee = group.reduce((s, l) => s + l.fee_amount, 0);
    const tax = group.reduce((s, l) => s + l.tax_amount, 0);
    fee_subtotal += fee;
    tax_subtotal += tax;
    consumption_tax += Math.floor(fee * 0.1);
    if (applyWithholding && map[profession]) {
      withholding_tax += computeWithholdingTax(fee);
    }
  }
  const total = fee_subtotal + tax_subtotal + consumption_tax - withholding_tax + adjustmentAmount;
  return { fee_subtotal, tax_subtotal, consumption_tax, withholding_tax, total };
}
function computeGenericInvoice(lines, adjustmentAmount = 0, applyWithholding = true, withholdingEligible = true) {
  const fee_subtotal = lines.reduce((s, l) => s + l.fee_amount, 0);
  const tax_subtotal = lines.reduce((s, l) => s + l.tax_amount, 0);
  const consumption_tax = Math.floor(fee_subtotal * 0.1);
  const withholding_tax = withholdingEligible && applyWithholding ? computeWithholdingTax(fee_subtotal) : 0;
  const total = fee_subtotal + tax_subtotal + consumption_tax - withholding_tax + adjustmentAmount;
  return { fee_subtotal, tax_subtotal, consumption_tax, withholding_tax, total };
}
function sumOnly(lines, adjustmentAmount) {
  return {
    fee_subtotal: lines.reduce((s, l) => s + l.fee_amount, 0),
    tax_subtotal: lines.reduce((s, l) => s + l.tax_amount, 0),
    consumption_tax: 0,
    withholding_tax: 0,
    total: lines.reduce((s, l) => s + l.fee_amount + l.tax_amount, adjustmentAmount)
  };
}
function computeDocument(docType, lines, adjustmentAmount = 0, applyWithholding = true, config, estimateProfessionWithholding) {
  switch (docType) {
    case "judicial_invoice":
      return computeJudicialInvoice(lines, adjustmentAmount, applyWithholding);
    case "administrative_invoice":
      return computeAdministrativeInvoice(lines, adjustmentAmount, applyWithholding);
    case "surveyor_invoice":
    case "guarantee_invoice":
      return computeSurveyorInvoice(lines, adjustmentAmount, applyWithholding);
    case "estimate":
      return computeEstimate(lines, adjustmentAmount, applyWithholding, estimateProfessionWithholding);
    case "land_calculation":
    case "building_calculation":
      return sumOnly(lines, adjustmentAmount);
    default:
      if (config?.kind === "invoice") {
        return computeGenericInvoice(lines, adjustmentAmount, applyWithholding, config.withholding_eligible);
      }
      return sumOnly(lines, adjustmentAmount);
  }
}

// src/fee-schedule.ts
import { z as z7 } from "zod";
var AreaTierSchema = z7.object({
  over: z7.number().min(0),
  // 下限（この値を超える面積に適用。Excel VLOOKUP の閾値列と同義）
  amount: z7.number().int().min(0)
  // 標準報酬額（円）
});
var FeePricingSchema = z7.discriminatedUnion("type", [
  // 固定単価 × 数量
  z7.object({ type: z7.literal("fixed"), unit_price: z7.number().int().min(0) }),
  // 面積段階テーブル参照。数量は乗算しない（Excel: J30 = VLOOKUP額そのまま。加減率のみ乗算）
  z7.object({ type: z7.literal("area_tiered"), table: z7.string() })
]);
var WorkRateItemSchema = z7.object({
  key: z7.string().min(1),
  section: z7.string().min(1),
  label: z7.string().min(1),
  /** 数量の単位表示（筆・件・点・区画・㎡・時間・通など） */
  unit: z7.string(),
  /** 摘要欄の既定文言 */
  note: z7.string().optional(),
  pricing: FeePricingSchema,
  /**
   * 加減率の効き方（Excel I列/J列）:
   * - multiplier: 率入力時 `数量×単価×率`（未入力は等倍）
   * - surcharge:  率入力時 `数量×単価 + 加算単価×率`（土地の申請手続 表示/分筆/地積変更/合筆/地目変更）
   */
  factor_mode: z7.enum(["multiplier", "surcharge"]).default("multiplier"),
  surcharge_unit_price: z7.number().int().min(0).optional()
});
var WorkRateScheduleSchema = z7.object({
  kind: z7.literal("work_rate"),
  sections: z7.array(z7.object({ key: z7.string().min(1), label: z7.string().min(1) })).min(1),
  items: z7.array(WorkRateItemSchema).min(1),
  /** 面積段階テーブル（key → 昇順の段階リスト） */
  area_tables: z7.record(z7.array(AreaTierSchema).min(1)),
  /** 旅費: 円/km */
  travel_unit_price: z7.number().int().min(0),
  /** 旅費から控除する km（建物=20、土地=0） */
  travel_deduct_km: z7.number().int().min(0).default(0),
  travel_note: z7.string().optional()
});
var GuaranteeScheduleSchema = z7.object({
  kind: z7.literal("guarantee"),
  base_fee: z7.number().int().min(0),
  // 基本報酬等（移転1件）
  inspection_unit_price: z7.number().int().min(0),
  // 閲覧 単価
  transcript_unit_price: z7.number().int().min(0),
  // 謄本（報酬側）単価
  count_surcharge_unit_price: z7.number().int().min(0),
  // 個数加算（税側）単価
  transcript_tax_unit_price: z7.number().int().min(0),
  // 謄本（税側）単価
  /** 登録免許税率（課税価格を1000円未満切捨 → ×率 → 100円未満切捨） */
  registration_tax_rate: z7.number().min(0)
});
var FeeScheduleSchema = z7.discriminatedUnion("kind", [
  WorkRateScheduleSchema,
  GuaranteeScheduleSchema
]);
var FEE_SCHEDULE_DOC_TYPES = [
  "land_calculation",
  "building_calculation",
  "guarantee_invoice"
];
function isFeeScheduleDocType(docType) {
  return FEE_SCHEDULE_DOC_TYPES.includes(docType);
}
var WorkRateInputRowSchema = z7.object({
  key: z7.string().min(1),
  /** fixed 行の数量。area_tiered 行では無視される */
  quantity: z7.number().min(0).default(1),
  /** area_tiered 行の面積㎡ */
  area: z7.number().min(0).optional(),
  /** 加減率（0 または未指定 = 等倍） */
  factor: z7.number().min(0).optional(),
  /** 表示の補足（「1階」など）。同一 key の複数行を区別する */
  label_suffix: z7.string().optional()
});
var WorkRateInputSchema = z7.object({
  kind: z7.literal("work_rate"),
  /** 所在 */
  location: z7.string().optional(),
  /** 所在欄の補足（地域区分・床面積表示など） */
  area_note: z7.string().optional(),
  rows: z7.array(WorkRateInputRowSchema).max(200).default([]),
  travel_km: z7.number().min(0).default(0),
  /** 調整額（正の値を報酬合計から減算。Excel: 合計報酬額① = 上記合計 − 調整額） */
  adjustment_amount: z7.number().int().default(0),
  /** 登録免許税・印紙税（立替） */
  registration_tax_amount: z7.number().int().min(0).default(0),
  /** 立替金（杭・標識・材料費・運搬費等） */
  advance_expense_amount: z7.number().int().min(0).default(0),
  /** 前受金（請求金額⑨ = 合計金額⑦ − 前受金⑧） */
  prepaid_amount: z7.number().int().min(0).default(0),
  /** 源泉徴収の適用（Excel「源泉する」ボタン相当。法人又は個人で給与支払いのある者） */
  apply_withholding: z7.boolean().default(false)
});
var GuaranteeDebtorSchema = z7.object({
  name: z7.string(),
  customer_number: z7.string().optional(),
  /** 移転金額（課税価格）。登録免許税の基礎 */
  transfer_amount: z7.number().int().min(0).default(0),
  /** 基本報酬等を計上するか（Excel: E列に値があると基本報酬が立つ） */
  has_base_fee: z7.boolean().default(true),
  inspection_count: z7.number().int().min(0).default(0),
  // 閲覧 個数（報酬側）
  count_surcharge: z7.number().int().min(0).default(0),
  // 個数加算 個数（税側）
  transcript_count: z7.number().int().min(0).default(0),
  // 謄本 個数（報酬側）
  transcript_tax_count: z7.number().int().min(0).default(0),
  // 謄本 個数（税側）
  per_diem: z7.number().int().min(0).default(0),
  // 日当
  travel: z7.number().int().min(0).default(0)
  // 旅費
});
var GuaranteeInputSchema = z7.object({
  kind: z7.literal("guarantee"),
  /** 宛先（愛知県信用保証協会 等）は documents.recipient_name を使う */
  debtors: z7.array(GuaranteeDebtorSchema).max(100).default([]),
  /** 仮払金額（振込金額 = 請求金額 − 仮払金額） */
  advance_paid_amount: z7.number().int().min(0).default(0)
});
var FeeCalcInputSchema = z7.discriminatedUnion("kind", [
  WorkRateInputSchema,
  GuaranteeInputSchema
]);
var LAND_AREA_TIERS = [
  { over: 0, amount: 43420 },
  { over: 100, amount: 54880 },
  { over: 200, amount: 63800 },
  { over: 300, amount: 71230 },
  { over: 400, amount: 83790 },
  { over: 600, amount: 94420 },
  { over: 800, amount: 103590 },
  { over: 1e3, amount: 140140 },
  { over: 2e3, amount: 168150 },
  { over: 3e3, amount: 191700 },
  { over: 4e3, amount: 212520 },
  { over: 5e3, amount: 228640 },
  { over: 6e3, amount: 244760 },
  { over: 7e3, amount: 260880 },
  { over: 8e3, amount: 277e3 },
  { over: 9e3, amount: 293120 },
  { over: 1e4, amount: 309240 },
  { over: 11e3, amount: 325360 },
  { over: 12e3, amount: 341480 },
  { over: 13e3, amount: 357600 },
  { over: 14e3, amount: 373720 },
  { over: 15e3, amount: 389840 },
  { over: 16e3, amount: 405960 },
  { over: 17e3, amount: 422080 },
  { over: 18e3, amount: 438200 },
  { over: 19e3, amount: 454320 },
  { over: 2e4, amount: 470440 },
  { over: 21e3, amount: 486560 },
  { over: 22e3, amount: 502680 },
  { over: 23e3, amount: 518800 },
  { over: 24e3, amount: 534920 },
  { over: 25e3, amount: 551040 },
  { over: 26e3, amount: 567160 },
  { over: 27e3, amount: 583280 },
  { over: 28e3, amount: 599400 },
  { over: 29e3, amount: 615520 },
  { over: 3e4, amount: 631640 },
  { over: 31e3, amount: 647760 },
  { over: 32e3, amount: 663880 },
  { over: 33e3, amount: 68e4 }
];
var BUILDING_AREA_TIERS = [
  { over: 0, amount: 14850 },
  { over: 50, amount: 19220 },
  { over: 100, amount: 25330 },
  { over: 200, amount: 3e4 },
  { over: 300, amount: 33980 },
  { over: 400, amount: 40580 },
  { over: 600, amount: 46210 },
  { over: 800, amount: 51060 },
  { over: 1e3, amount: 70380 },
  { over: 2e3, amount: 85240 },
  { over: 3e3, amount: 97660 },
  { over: 4e3, amount: 107360 }
];
var fixed = (key, section, label, unit, unit_price, note) => ({
  key,
  section,
  label,
  unit,
  note,
  pricing: { type: "fixed", unit_price },
  factor_mode: "multiplier"
});
var surcharge = (key, section, label, unit, unit_price, surcharge_unit_price, note) => ({
  key,
  section,
  label,
  unit,
  note,
  pricing: { type: "fixed", unit_price },
  factor_mode: "surcharge",
  surcharge_unit_price
});
var DEFAULT_LAND_CALCULATION_SCHEDULE = {
  kind: "work_rate",
  sections: [
    { key: "research", label: "\u8ABF\u67FB\u696D\u52D9" },
    { key: "measurement", label: "\u6E2C\u91CF\u696D\u52D9" },
    { key: "application", label: "\u7533\u8ACB\u624B\u7D9A" },
    { key: "other", label: "\u5BE9\u67FB\u8ACB\u6C42\u30FB\u76F8\u8AC7\u30FB\u66F8\u985E\u4F5C\u6210\u7B49" }
  ],
  items: [
    // 1-1) 資料調査
    fixed("record_search", "research", "\u8CC7\u6599\u8ABF\u67FB \u516C\u7C3F\u985E", "\u7B46", 1060),
    fixed("map_search", "research", "\u8CC7\u6599\u8ABF\u67FB \u5730\u56F3\u985E", "\u7B46", 1060),
    fixed("drawing_search", "research", "\u8CC7\u6599\u8ABF\u67FB \u56F3\u9762\u985E", "\u7B46", 2230),
    fixed("evidence_doc", "research", "\u8CC7\u6599\u8ABF\u67FB \u758E\u660E\u66F8\u9762", "\u4EF6", 4460, "\u53D7\u8A17\u4E8B\u4EF6\uFF11\u4EF6\u3054\u3068"),
    // 1-2) 現地調査
    fixed("pre_survey", "research", "\u4E8B\u524D\u8ABF\u67FB", "\u4EF6", 32030, "\u53D7\u8A17\u4E8B\u4EF6\uFF11\u4EF6\u3054\u3068"),
    fixed("traverse_survey", "research", "\u7B46\u754C\u78BA\u8A8D \u591A\u89D2\u6E2C\u91CF", "\u70B9", 18930),
    fixed("restoration_survey", "research", "\u7B46\u754C\u78BA\u8A8D \u5FA9\u5143\u6E2C\u91CF", "\u70B9", 12230),
    fixed("parcel_adjust_basic", "research", "\u7B46\u754C\u78BA\u8A8D \u753B\u5730\u8ABF\u6574\uFF08\u57FA\u672C\uFF09", "\u533A\u753B", 22810),
    fixed("parcel_adjust_add", "research", "\u7B46\u754C\u78BA\u8A8D \u753B\u5730\u8ABF\u6574\uFF08\u52A0\u7B97\uFF09", "\u533A\u753B", 15140),
    fixed("private_witness", "research", "\u7ACB\u4F1A \u6C11\u6709\u5730 \u7ACB\u4F1A\u30FB\u78BA\u8A8D", "\u70B9", 7470),
    fixed("private_measure", "research", "\u7ACB\u4F1A \u6C11\u6709\u5730 \u6E2C\u8DDD\u30FB\u63A2\u7D22", "\u70B9", 1e4),
    fixed("private_special", "research", "\u7ACB\u4F1A \u6C11\u6709\u5730 \u7279\u6B8A\u4F5C\u696D", "\u70B9", 12420),
    fixed("public_rank_a", "research", "\u7ACB\u4F1A \u516C\u6709\u5730 \uFF21\u30E9\u30F3\u30AF", "\u70B9", 16110),
    fixed("public_rank_b", "research", "\u7ACB\u4F1A \u516C\u6709\u5730 \uFF22\u30E9\u30F3\u30AF", "\u70B9", 53980),
    fixed("public_rank_c", "research", "\u7ACB\u4F1A \u516C\u6709\u5730 \uFF23\u30E9\u30F3\u30AF", "\u70B9", 1060),
    // 2) 測量業務
    {
      key: "area_survey",
      section: "measurement",
      label: "\u9762\u7A4D\u6E2C\u91CF",
      unit: "\u33A1",
      pricing: { type: "area_tiered", table: "land_area" },
      factor_mode: "multiplier"
    },
    fixed("boundary_point_setting", "measurement", "\u6A19\u8B58\u8A2D\u7F6E \u5883\u754C\u70B9\u6E2C\u8A2D", "\u70B9", 10090),
    fixed("boundary_marker", "measurement", "\u6A19\u8B58\u8A2D\u7F6E \u5883\u754C\u6A19\u8A2D\u7F6E", "\u70B9", 11160, "\uFF08\u5831\u544A\u66F8\u4F5C\u6210\u696D\u52D9\u542B\u3080\uFF09"),
    fixed("reference_point", "measurement", "\u6A19\u8B58\u8A2D\u7F6E \u5F15\u7167\u70B9\u6E2C\u91CF", "\u70B9", 13780),
    // 3) 申請手続（表示/分筆/地積変更/合筆/地目変更は 加算単価×率 型）
    surcharge("registration_display", "application", "\u8868\u793A", "\u4EF6", 29310, 4850, "\uFF08\u8ABF\u66F8\u3001\u5730\u5F62\u56F3\u542B\u3080\uFF09"),
    surcharge("subdivision", "application", "\u5206\u7B46", "\u4EF6", 30760, 4850, "\uFF08\u8ABF\u66F8\u3001\u5730\u5F62\u56F3\u542B\u3080\uFF09"),
    fixed("subdivision_simultaneous", "application", "\u5206\u7B46\uFF08\u540C\u6642\u7533\u8ACB\uFF09", "\u4EF6", 21060, "\uFF08\u540C\u6642\u7533\u8ACB\uFF09"),
    surcharge("area_correction", "application", "\u5730\u7A4D\u306E\u5909\u66F4\u30FB\u66F4\u6B63", "\u4EF6", 23e3, 10380, "\uFF08\u8ABF\u66F8\u542B\u3080\uFF09"),
    surcharge("consolidation", "application", "\u5408\u7B46", "\u4EF6", 33770, 1160, "\uFF08\u8ABF\u66F8\u3001\u5730\u5F62\u56F3\u3001\u73FE\u5730\u8ABF\u67FB\u8CBB\u542B\u3080\uFF09"),
    surcharge("land_category_change", "application", "\u5730\u76EE\u306E\u5909\u66F4", "\u4EF6", 28920, 1160, "\uFF08\u8ABF\u66F8\u3001\u73FE\u5730\u8ABF\u67FB\u8CBB\u542B\u3080\uFF09"),
    fixed("field_investigation", "application", "\u73FE\u5730\u8ABF\u67FB\u8CBB", "\u4EF6", 14850, "\u5B9F\u5730\u8ABF\u67FB\u306E\u7ACB\u4F1A"),
    // 4〜6) 審査請求・相談・書類作成等
    fixed("appeal", "other", "\u5BE9\u67FB\u8ACB\u6C42", "\u4EF6", 16210),
    fixed("consultation", "other", "\u76F8\u8AC7", "\u6642\u9593", 3590, "\u4E8B\u4EF6\u53D7\u8A17\u3092\u4F34\u3046\u5834\u5408\u3092\u9664\u304F"),
    fixed("doc_with_draft", "other", "\u66F8\u985E\u306E\u4F5C\u6210\uFF08\u6587\u6848\u3092\u8981\u3059\u308B\u3082\u306E\uFF09", "\u901A", 4850),
    fixed("doc_without_draft", "other", "\u66F8\u985E\u306E\u4F5C\u6210\uFF08\u6587\u6848\u3092\u8981\u3057\u306A\u3044\u3082\u306E\uFF09", "\u901A", 2420),
    fixed("doc_copy", "other", "\u66F8\u985E\u306E\u4F5C\u6210\uFF08\u539F\u672C\u306E\u8907\u88FD\uFF09", "\u901A", 970)
  ],
  area_tables: { land_area: LAND_AREA_TIERS },
  travel_unit_price: 200,
  travel_deduct_km: 0,
  travel_note: "\uFF08\u5F80\u5FA9\u2212\uFF12\uFF10\uFF4B\uFF4D\uFF09\xD7\u56DE\uFF1D\uFF4B\uFF4D"
};
var DEFAULT_BUILDING_CALCULATION_SCHEDULE = {
  kind: "work_rate",
  sections: [
    { key: "research", label: "\u8ABF\u67FB\u696D\u52D9" },
    { key: "measurement", label: "\u6E2C\u91CF\u696D\u52D9" },
    { key: "application", label: "\u7533\u8ACB\u624B\u7D9A" },
    { key: "other", label: "\u5BE9\u67FB\u8ACB\u6C42\u30FB\u76F8\u8AC7\u30FB\u66F8\u985E\u4F5C\u6210\u7B49" },
    { key: "incidental", label: "\u9644\u968F\u696D\u52D9" }
  ],
  items: [
    // 1) 調査業務
    fixed("record_search", "research", "\u8CC7\u6599\u8ABF\u67FB \u516C\u7C3F\u985E", "\u7B46\u30FB\u500B", 1060),
    fixed("map_search", "research", "\u8CC7\u6599\u8ABF\u67FB \u5730\u56F3\u985E", "\u7B46", 1060),
    fixed("drawing_search", "research", "\u8CC7\u6599\u8ABF\u67FB \u56F3\u9762\u985E", "\u7B46\u30FB\u500B", 2230),
    fixed("evidence_doc", "research", "\u8CC7\u6599\u8ABF\u67FB \u758E\u660E\u66F8\u9762", "\u4EF6", 4460),
    fixed("site_witness", "research", "\u73FE\u5730\u8ABF\u67FB \uFF21\u7ACB\u4F1A\u30FB\u78BA\u8A8D", "\u70B9", 7470, "\u53D7\u8A17\u4E8B\u4EF6\uFF11\u4EF6\u3054\u3068"),
    fixed("site_measure", "research", "\u73FE\u5730\u8ABF\u67FB \uFF22\u6E2C\u8DDD\u30FB\u63A2\u7D22", "\u70B9", 1e4),
    fixed("site_special", "research", "\u73FE\u5730\u8ABF\u67FB \uFF23\u7279\u6B8A\u4F5C\u696D", "\u70B9", 12420),
    // 2) 測量業務（床面積測量: 階ごとに1行入力する）
    {
      key: "floor_area_survey",
      section: "measurement",
      label: "\u5E8A\u9762\u7A4D\u6E2C\u91CF",
      unit: "\u33A1",
      pricing: { type: "area_tiered", table: "building_floor_area" },
      factor_mode: "multiplier"
    },
    // 3) 申請手続
    fixed("registration_display", "application", "\u8868\u793A", "\u4EF6", 24460, "\uFF08\u6DFB\u4ED8\u66F8\u985E\u7B49\uFF09"),
    fixed("floor_area_correction", "application", "\u5E8A\u9762\u7A4D\u306E\u5909\u66F4\u30FB\u66F4\u6B63", "\u4EF6", 24460, "\uFF08\u6DFB\u4ED8\u66F8\u985E\u7B49\uFF09"),
    fixed("demolition", "application", "\u6EC5\u5931", "\u4EF6", 28920, "\uFF08\u6DFB\u4ED8\u66F8\u985E\u7B49\uFF09"),
    fixed("floor_count_surcharge", "application", "\u968E\u6570\u52A0\u7B97", "\u968E", 4460),
    fixed("annex_surcharge", "application", "\u4ED8\u5C5E\u5EFA\u7269\u52A0\u7B97", "\u68DF", 5630),
    fixed("field_investigation", "application", "\u73FE\u5730\u8ABF\u67FB\u8CBB", "\u4EF6", 14850, "\u5B9F\u5730\u8ABF\u67FB\u306E\u7ACB\u4F1A"),
    // 4〜6) 審査請求・相談・書類作成等
    fixed("appeal", "other", "\u5BE9\u67FB\u8ACB\u6C42", "\u4EF6", 16210),
    fixed("consultation", "other", "\u76F8\u8AC7", "\u6642\u9593", 3590, "\u4E8B\u4EF6\u53D7\u8A17\u3092\u4F34\u3046\u5834\u5408\u3092\u9664\u304F"),
    fixed("doc_with_draft", "other", "\u66F8\u985E\u306E\u4F5C\u6210\uFF08\u6587\u6848\u3092\u8981\u3059\u308B\u3082\u306E\uFF09", "\u901A", 4850),
    fixed("doc_without_draft", "other", "\u66F8\u985E\u306E\u4F5C\u6210\uFF08\u6587\u6848\u3092\u8981\u3057\u306A\u3044\u3082\u306E\uFF09", "\u901A", 2420),
    fixed("doc_copy", "other", "\u66F8\u985E\u306E\u4F5C\u6210\uFF08\u539F\u672C\u306E\u8907\u88FD\uFF09", "\u901A", 970),
    // 7) 附随業務
    fixed("land_category_change", "incidental", "\u5730\u76EE\u5909\u66F4", "\u4EF6", 28920)
  ],
  area_tables: { building_floor_area: BUILDING_AREA_TIERS },
  travel_unit_price: 200,
  travel_deduct_km: 20,
  travel_note: "\uFF08\u5F80\u5FA9\u2212\uFF12\uFF10\uFF4B\uFF4D\uFF09\xD7\u56DE\uFF1D\uFF4B\uFF4D"
};
var DEFAULT_GUARANTEE_SCHEDULE = {
  kind: "guarantee",
  base_fee: 13200,
  inspection_unit_price: 970,
  transcript_unit_price: 500,
  count_surcharge_unit_price: 331,
  transcript_tax_unit_price: 480,
  registration_tax_rate: 2e-3
};
var DEFAULT_FEE_SCHEDULES = {
  land_calculation: DEFAULT_LAND_CALCULATION_SCHEDULE,
  building_calculation: DEFAULT_BUILDING_CALCULATION_SCHEDULE,
  guarantee_invoice: DEFAULT_GUARANTEE_SCHEDULE
};
function expectedScheduleKind(docType) {
  return docType === "guarantee_invoice" ? "guarantee" : "work_rate";
}
function lookupAreaTier(tiers, area) {
  let amount = 0;
  for (const t of tiers) {
    if (area > t.over) amount = t.amount;
    else break;
  }
  return amount;
}
function computeWorkRate(schedule, input) {
  const itemMap = new Map(schedule.items.map((i) => [i.key, i]));
  const rows = [];
  const sectionSubtotals = {};
  for (const s of schedule.sections) sectionSubtotals[s.key] = 0;
  for (const row of input.rows) {
    const item = itemMap.get(row.key);
    if (!item) continue;
    let base = 0;
    let quantity = 0;
    let amount = 0;
    const factor = row.factor && row.factor !== 0 ? row.factor : null;
    if (item.pricing.type === "area_tiered") {
      const tiers = schedule.area_tables[item.pricing.table] ?? [];
      quantity = row.area ?? 0;
      if (quantity <= 0) continue;
      base = lookupAreaTier(tiers, quantity);
      amount = factor === null ? base : Math.floor(base * factor);
    } else {
      quantity = row.quantity;
      if (quantity <= 0) continue;
      base = item.pricing.unit_price;
      if (factor === null) {
        amount = Math.floor(quantity * base);
      } else if (item.factor_mode === "surcharge") {
        amount = Math.floor(quantity * base + (item.surcharge_unit_price ?? 0) * factor);
      } else {
        amount = Math.floor(quantity * base * factor);
      }
    }
    const label = row.label_suffix ? `${item.label}\uFF08${row.label_suffix}\uFF09` : item.label;
    rows.push({
      key: item.key,
      section: item.section,
      label,
      unit: item.unit,
      quantity,
      base_amount: base,
      factor,
      amount,
      note: item.note ?? ""
    });
    sectionSubtotals[item.section] = (sectionSubtotals[item.section] ?? 0) + amount;
  }
  const travelKm = Math.max(input.travel_km - schedule.travel_deduct_km, 0);
  const travelAmount = Math.floor(travelKm * schedule.travel_unit_price);
  const workTotal = rows.reduce((s, r) => s + r.amount, 0);
  const grossTotal = workTotal + travelAmount;
  const feeTotal = grossTotal - input.adjustment_amount;
  const consumptionTax = Math.floor(Math.max(feeTotal, 0) * 0.1);
  const subtotalWithTax = feeTotal + consumptionTax;
  const withholdingTax = input.apply_withholding ? computeWithholdingTax(feeTotal) : 0;
  const netAfterWithholding = subtotalWithTax - withholdingTax;
  const expenseSubtotal = input.registration_tax_amount + input.advance_expense_amount;
  const grandTotal = netAfterWithholding + expenseSubtotal;
  const billedTotal = grandTotal - input.prepaid_amount;
  return {
    kind: "work_rate",
    rows,
    section_subtotals: sectionSubtotals,
    travel_km: input.travel_km,
    travel_amount: travelAmount,
    gross_total: grossTotal,
    adjustment_amount: input.adjustment_amount,
    fee_total: feeTotal,
    consumption_tax: consumptionTax,
    subtotal_with_tax: subtotalWithTax,
    withholding_tax: withholdingTax,
    net_after_withholding: netAfterWithholding,
    registration_tax_amount: input.registration_tax_amount,
    advance_expense_amount: input.advance_expense_amount,
    expense_subtotal: expenseSubtotal,
    grand_total: grandTotal,
    prepaid_amount: input.prepaid_amount,
    billed_total: billedTotal
  };
}
function computeRegistrationTax(transferAmount, rate) {
  const taxBase = Math.floor(transferAmount / 1e3) * 1e3;
  return Math.floor(taxBase * rate / 100) * 100;
}
function computeGuarantee(schedule, input) {
  const blocks = [];
  for (const d of input.debtors) {
    const baseFee = d.has_base_fee ? schedule.base_fee : 0;
    const registrationTax = d.transfer_amount > 0 ? computeRegistrationTax(d.transfer_amount, schedule.registration_tax_rate) : 0;
    const inspectionAmount = d.inspection_count * schedule.inspection_unit_price;
    const countSurchargeAmount = d.count_surcharge * schedule.count_surcharge_unit_price;
    const transcriptAmount = d.transcript_count * schedule.transcript_unit_price;
    const transcriptTaxAmount = d.transcript_tax_count * schedule.transcript_tax_unit_price;
    const feeTotal2 = baseFee + inspectionAmount + transcriptAmount + d.per_diem + d.travel;
    const taxTotal2 = registrationTax + countSurchargeAmount + transcriptTaxAmount;
    if (feeTotal2 === 0 && taxTotal2 === 0) continue;
    const consumptionTax = Math.floor(feeTotal2 * 0.1);
    const blockTotal = feeTotal2 + taxTotal2 + consumptionTax;
    const withholdingTax = computeWithholdingTax(feeTotal2);
    const billedTotal2 = blockTotal - withholdingTax;
    blocks.push({
      name: d.name,
      customer_number: d.customer_number ?? "",
      transfer_amount: d.transfer_amount,
      base_fee: baseFee,
      registration_tax: registrationTax,
      inspection_amount: inspectionAmount,
      count_surcharge_amount: countSurchargeAmount,
      transcript_amount: transcriptAmount,
      transcript_tax_amount: transcriptTaxAmount,
      per_diem: d.per_diem,
      travel: d.travel,
      fee_total: feeTotal2,
      tax_total: taxTotal2,
      consumption_tax: consumptionTax,
      block_total: blockTotal,
      withholding_tax: withholdingTax,
      billed_total: billedTotal2
    });
  }
  const grandTotal = blocks.reduce((s, b) => s + b.block_total, 0);
  const feeTotal = blocks.reduce((s, b) => s + b.fee_total, 0);
  const taxTotal = blocks.reduce((s, b) => s + b.tax_total, 0);
  const consumptionTaxTotal = blocks.reduce((s, b) => s + b.consumption_tax, 0);
  const withholdingTotal = blocks.reduce((s, b) => s + b.withholding_tax, 0);
  const billedTotal = grandTotal - withholdingTotal;
  const inspectionStampTotal = blocks.reduce((s, b) => s + b.count_surcharge_amount, 0);
  const transcriptStampTotal = blocks.reduce((s, b) => s + b.transcript_tax_amount, 0);
  const registrationTaxTotal = blocks.reduce((s, b) => s + b.registration_tax, 0);
  return {
    kind: "guarantee",
    blocks,
    grand_total: grandTotal,
    fee_total: feeTotal,
    tax_total: taxTotal,
    consumption_tax_total: consumptionTaxTotal,
    withholding_total: withholdingTotal,
    billed_total: billedTotal,
    advance_paid_amount: input.advance_paid_amount,
    transfer_net_total: billedTotal - input.advance_paid_amount,
    inspection_stamp_total: inspectionStampTotal,
    transcript_stamp_total: transcriptStampTotal,
    stamp_total: inspectionStampTotal + transcriptStampTotal,
    registration_tax_total: registrationTaxTotal,
    tax_breakdown_total: inspectionStampTotal + transcriptStampTotal + registrationTaxTotal
  };
}
function computeFeeCalc(schedule, input) {
  if (schedule.kind === "work_rate" && input.kind === "work_rate") {
    return computeWorkRate(schedule, input);
  }
  if (schedule.kind === "guarantee" && input.kind === "guarantee") {
    return computeGuarantee(schedule, input);
  }
  throw new Error(`fee schedule kind mismatch: schedule=${schedule.kind} input=${input.kind}`);
}
function projectFeeCalcLines(result) {
  const lines = [];
  let order = 1;
  if (result.kind === "work_rate") {
    for (const r of result.rows) {
      lines.push({
        line_order: order++,
        description: r.unit ? `${r.label}\uFF08${formatQuantity(r.quantity)}${r.unit}\uFF09` : r.label,
        fee_amount: r.amount,
        tax_amount: 0
      });
    }
    if (result.travel_amount > 0) {
      lines.push({
        line_order: order++,
        description: `\u65C5\u8CBB\uFF08${formatQuantity(result.travel_km)}\uFF4B\uFF4D\uFF09`,
        fee_amount: result.travel_amount,
        tax_amount: 0
      });
    }
    if (result.registration_tax_amount > 0) {
      lines.push({
        line_order: order++,
        description: "\u767B\u9332\u514D\u8A31\u7A0E\u30FB\u5370\u7D19\u7A0E",
        fee_amount: 0,
        tax_amount: result.registration_tax_amount
      });
    }
    if (result.advance_expense_amount > 0) {
      lines.push({
        line_order: order++,
        description: "\u7ACB\u66FF\u91D1",
        fee_amount: 0,
        tax_amount: result.advance_expense_amount
      });
    }
  } else {
    for (const b of result.blocks) {
      lines.push({
        line_order: order++,
        description: b.customer_number ? `${b.name}\uFF08\u9867\u5BA2\u756A\u53F7 ${b.customer_number}\uFF09` : b.name,
        fee_amount: b.fee_total,
        tax_amount: b.tax_total
      });
    }
  }
  return lines;
}
function formatQuantity(n) {
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}
function feeCalcResultToTotals(result) {
  if (result.kind === "work_rate") {
    return {
      fee_subtotal: result.fee_total,
      tax_subtotal: result.expense_subtotal,
      consumption_tax: result.consumption_tax,
      withholding_tax: result.withholding_tax,
      total: result.billed_total
    };
  }
  return {
    fee_subtotal: result.fee_total,
    tax_subtotal: result.tax_total,
    consumption_tax: result.consumption_tax_total,
    withholding_tax: result.withholding_total,
    total: result.billed_total
  };
}

// src/usage-events.ts
import { z as z8 } from "zod";
var USAGE_SCREEN_NAMES = [
  "dashboard",
  "clients",
  "client_detail",
  "case_detail",
  "case_pdfs",
  "document",
  "settings",
  "profession_master",
  "work_item_master",
  "fee_schedules",
  "templates",
  "template_new",
  "template_library",
  "template_edit"
];
var USAGE_FEATURE_NAMES = [
  "template_edit",
  "ai_generate",
  "pdf_import",
  "document_create"
];
var screen = z8.enum(USAGE_SCREEN_NAMES);
var shortStr = (max) => z8.string().min(1).max(max);
var USAGE_EVENT_SCHEMAS = {
  // ── サーバー側で記録（source='api'）──────────────────────────────
  /** 書類発行成功 */
  document_issued: z8.object({ doc_type: shortStr(40), from_work_items: z8.boolean().optional() }).strict(),
  /** 修正版作成 */
  document_revised: z8.object({ doc_type: shortStr(40) }).strict(),
  /** 実施業務からの一括生成 */
  documents_generated: z8.object({ doc_types: z8.array(shortStr(40)).max(10), count: z8.number().int().min(0) }).strict(),
  /** PDF生成完了 */
  pdf_generated: z8.object({
    doc_type: shortStr(40),
    duration_ms: z8.number().int().min(0),
    renderer: z8.enum(["browser", "pdf-lib"])
  }).strict(),
  /** AI呼び出し完了（tokens は入れない。集計を usage_daily に一本化するための薄い記録） */
  ai_operation: z8.object({ kind: shortStr(40), applied: z8.boolean() }).strict(),
  /** テンプレート保存 */
  template_saved: z8.object({
    doc_type: shortStr(40),
    preset_key: shortStr(60).nullable().optional(),
    is_customized: z8.boolean()
  }).strict(),
  /** テンプレ新規作成 */
  template_created: z8.object({ method: z8.enum(["library", "blank", "ai", "pdf_import"]) }).strict(),
  /** 認証成功（同一ユーザーは日1回に間引く） */
  user_login: z8.object({}).strict(),
  // ── フロントから送信（source='web'。POST /api/events 経由）────────
  /** 主要画面の表示 */
  screen_view: z8.object({ screen }).strict(),
  /** 開始したが完了せず離脱 */
  feature_abandoned: z8.object({ feature: z8.enum(USAGE_FEATURE_NAMES), step: shortStr(40).optional() }).strict(),
  /** 一覧の検索・フィルタ利用 */
  search_used: z8.object({ screen, has_result: z8.boolean() }).strict(),
  /** フロント例外（メッセージ本文は入れない: PII混入防止） */
  client_error: z8.object({ screen: screen.optional(), error_name: shortStr(80) }).strict()
};
var USAGE_EVENT_NAMES = Object.keys(USAGE_EVENT_SCHEMAS);
var WEB_USAGE_EVENT_NAMES = [
  "screen_view",
  "feature_abandoned",
  "search_used",
  "client_error"
];
var UsageEventBeaconSchema = z8.object({
  events: z8.array(
    z8.object({
      name: z8.enum(WEB_USAGE_EVENT_NAMES),
      properties: z8.record(z8.unknown()).optional(),
      /** 発生時刻（ISO 8601）。省略時・不正時はサーバー受信時刻 */
      occurred_at: z8.string().datetime({ offset: true }).optional()
    })
  ).min(1).max(20)
});
export {
  AiTemplateEditClarifyResponseSchema,
  AiTemplateEditPatchResponseSchema,
  AiTemplateEditRequestSchema,
  AiTemplateEditResponseSchema,
  AiTemplateGenerateClarifyResponseSchema,
  AiTemplateGenerateRequestSchema,
  AiTemplateGenerateResponseSchema,
  AiTemplateGeneratedResponseSchema,
  AiTemplateImportRequestSchema,
  AiTemplateImportResponseSchema,
  AreaTierSchema,
  ChatTurnSchema,
  CreateCustomerSchema,
  CreateDocumentSchema,
  CustomerSchema,
  DEFAULT_BUILDING_CALCULATION_SCHEDULE,
  DEFAULT_FEE_SCHEDULES,
  DEFAULT_GUARANTEE_SCHEDULE,
  DEFAULT_LAND_CALCULATION_SCHEDULE,
  DEFAULT_TRANSFER_NOTE,
  DisplayFormatEnum,
  DisplayPositionEnum,
  DocumentLineSchema,
  DocumentSchema,
  DocumentStatusSchema,
  DocumentTypeKindEnum,
  DocumentTypeSchema,
  FEE_SCHEDULE_DOC_TYPES,
  FeeCalcInputSchema,
  FeePricingSchema,
  FeeScheduleSchema,
  FileTypeSchema,
  GeneratePdfSchema,
  GenerateRequestSchema,
  GuaranteeDebtorSchema,
  GuaranteeInputSchema,
  GuaranteeScheduleSchema,
  InvoiceTemplateDataSchema,
  JsonPatchOpSchema,
  JsonPatchSchema,
  PROFESSION_COLOR,
  PROFESSION_LABEL,
  PdfResultSchema,
  ProfessionEnum,
  ProfessionHolderInputSchema,
  ProfessionHolderPatchSchema,
  ProfessionHolderSchema,
  ProfessionRuleInputSchema,
  ProfessionRulePatchSchema,
  ProfessionRuleSchema,
  ReorderSchema,
  TAX_CATEGORY_LABEL,
  TaxCategoryEnum,
  TaxCategorySchema,
  TenantDocumentTypeCreateSchema,
  TenantDocumentTypeSchema,
  TenantDocumentTypeUpdateSchema,
  TenantProfessionCreateSchema,
  TenantProfessionInputSchema,
  TenantProfessionSchema,
  USAGE_EVENT_NAMES,
  USAGE_EVENT_SCHEMAS,
  USAGE_FEATURE_NAMES,
  USAGE_SCREEN_NAMES,
  UpdateCustomerSchema,
  UsageEventBeaconSchema,
  VersionStatusSchema,
  WEB_USAGE_EVENT_NAMES,
  WORK_CATEGORY_LABEL,
  WorkCategoryEnum,
  WorkItemBulkInputSchema,
  WorkItemInputSchema,
  WorkItemSourceEnum,
  WorkRateInputRowSchema,
  WorkRateInputSchema,
  WorkRateItemSchema,
  WorkRateScheduleSchema,
  applySimpleOffice,
  applySimpleSettings,
  computeAdministrativeInvoice,
  computeDocument,
  computeEstimate,
  computeFeeCalc,
  computeGenericInvoice,
  computeGuarantee,
  computeJudicialInvoice,
  computeRegistrationTax,
  computeSurveyorInvoice,
  computeWithholdingTax,
  computeWorkRate,
  expectedScheduleKind,
  feeCalcResultToTotals,
  formatJPY,
  formatJSTDate,
  formatJSTDateTime,
  formatWareki,
  getDefaultIsPassThrough,
  getDefaultTaxCategory,
  getDefaultWithholdingTarget,
  isFeeScheduleDocType,
  lookupAreaTier,
  projectFeeCalcLines,
  renderBlock,
  renderFeeCalcSheetHtml,
  serializeBlocksToHtml
};
